import React from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import Button from '@teetsh/app/src/components/Buttons/Button';
import DiffView from 'components/DiffView';
import { trackEvent } from '@teetsh/app/src/queries/user/track';
import { useAIGenerationContext } from '@teetsh/app/src/components/Editor/AIAssist/contexts/AIGenerationContext';
import { ImprovementType } from '../../types';
import { SelectionResultPanel } from './SelectionResultPanel';
import 'twin.macro';

interface Props {
  data: string;
  originalText: string;
  onApply: () => void;
  onIgnore: () => void;
  isLoading?: boolean;
  diffMode?: 'words' | 'chars';
  action: ImprovementType;
}

export function SelectionAIResult({
  data,
  originalText,
  onApply,
  onIgnore,
  isLoading = false,
  diffMode = 'words',
  action,
}: Props) {
  const { t } = useTranslation(['common']);

  let trackingContext: string | undefined;
  try {
    const context = useAIGenerationContext();
    trackingContext = context.trackingContext;
  } catch {
    // Context not available, tracking will be skipped
  }

  function handleApply() {
    if (trackingContext) {
      trackEvent(`${trackingContext}:AIAssist:insert`, {
        action,
      });
    }
    onApply();
  }

  function handleIgnore() {
    if (trackingContext) {
      trackEvent(`${trackingContext}:AIAssist:ignore`, {
        action,
      });
    }
    onIgnore();
  }

  return (
    <SelectionResultPanel
      title={t('common:buttons.suggestionIA')}
      icon={SparklesIcon}
      footer={
        !isLoading && (
          <>
            <Button onClick={handleIgnore} disabled={isLoading} minimal>
              {t('common:buttons.ignore')}
            </Button>
            <Button onClick={handleApply} intent="primary" disabled={isLoading}>
              {t('common:buttons.replace')}
            </Button>
          </>
        )
      }
    >
      <div tw="p-4 text-sm max-w-md max-h-[10rem] overflow-y-auto">
        <DiffView
          originalText={originalText}
          newText={data}
          mode={diffMode}
          isLoading={isLoading}
        />
      </div>
    </SelectionResultPanel>
  );
}
